/* eslint-disable react-hooks/exhaustive-deps */
import {
  Badge,
  Box,
  Flex,
  FormControl,
  Icon,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { Icons } from "../../../../WinterCountIconFIle";
import InputMask from "react-input-mask";
import Select from "react-select";
import countryList from "react-select-country-list";
import scus from "state-counties-us";
import {
  addressTypeOptions,
  usStateOptions,
} from "../../../../utils/case/usStateOption";

const modernSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "34px",
    fontSize: "13px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#3B82F6" : "#E2E8F0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
    backgroundColor: "#FAFBFF",
    transition: "all 0.2s",
    "&:hover": { borderColor: "#94A3B8" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "13px",
    backgroundColor: state.isSelected
      ? "#3B82F6"
      : state.isFocused
        ? "#EFF6FF"
        : "white",
    color: state.isSelected ? "white" : "#1E293B",
    padding: "7px 12px",
  }),
  placeholder: (base) => ({ ...base, color: "#94A3B8", fontSize: "13px" }),
  singleValue: (base) => ({ ...base, fontSize: "13px", color: "#1E293B" }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#EFF6FF",
    borderRadius: "6px",
  }),
  multiValueLabel: (base) => ({ ...base, color: "#3B82F6", fontSize: "12px" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#93C5FD",
    "&:hover": { backgroundColor: "#DBEAFE", color: "#3B82F6" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "10px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
    border: "1px solid #E2E8F0",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const modernInputStyle = {
  fontSize: "13px",
  height: "34px",
  borderRadius: "8px",
  borderColor: "#E2E8F0",
  bg: "#FAFBFF",
  _focus: {
    borderColor: "blue.400",
    boxShadow: "0 0 0 3px rgba(59,130,246,0.15)",
  },
  _hover: { borderColor: "gray.300" },
};

const FieldLabel = ({ children }) => (
  <Text
    mb="5px"
    fontSize="11px"
    fontWeight="600"
    color="gray.500"
    letterSpacing="0.05em"
    textTransform="uppercase"
  >
    {children}
  </Text>
);

export default function AddressFormSection({
  addressFields,
  prependAddress,
  removeAddress,
  register,
  setValue,
  control,
}) {
  const toast = useToast();
  const addresses = useWatch({ name: "ADDRESS", control });
  const [suggestedAddresses, setSuggestedAddresses] = useState({});
  const [verifyingIndexes, setVerifyingIndexes] = useState([]);
  const skipVerificationRef = useRef(new Set());
  const prevVerifiableFields = useRef({});
  const countryOptions = useMemo(() => countryList().getData(), []);
  const [countyOptionsMap, setCountyOptionsMap] = useState({});

  useEffect(() => {
    const timeouts = [];
    addresses?.forEach((addr, index) => {
      const { STREET_1, STREET_2, CITY, STATE, ZIP } = addr || {};
      const isVerifiable = STREET_1 && STATE && (CITY || ZIP);
      const prev = prevVerifiableFields.current[index] || {};
      const hasChanged =
        prev.STREET_1 !== STREET_1 ||
        prev.CITY !== CITY ||
        prev.STATE !== STATE ||
        prev.ZIP !== ZIP;

      if (
        !isVerifiable ||
        !hasChanged ||
        skipVerificationRef.current.has(index)
      ) {
        skipVerificationRef.current.delete(index);
        return;
      }

      prevVerifiableFields.current[index] = {
        STREET_1,
        STREET_2,
        CITY,
        STATE,
        ZIP,
      };
      const timeout = setTimeout(() => verifyAddress(index), 1000);
      timeouts.push(timeout);
    });
    return () => timeouts.forEach(clearTimeout);
  }, [addresses]);

  const verifyAddress = async (index) => {
    setVerifyingIndexes((prev) => [...prev, index]);
    const values = addresses[index];
    const { STREET_1, CITY, STATE, ZIP, STREET_2 } = values;
    const query = `STREET1=${STREET_1?.trim() || ""}&STREET2=${STREET_2?.trim() || ""}&CITY=${CITY?.trim() || ""}&STATE=${STATE || ""}&ZIP=${ZIP || ""}`;

    try {
      const response = await fetch(`/api/getAddress?${query}`, {
        method: "GET",
        headers: { Accept: "text/plain; charset=utf-8" },
      });
      const text = await response.text();

      if (!response.ok) {
        if (response.status === 400) {
          setSuggestedAddresses((prev) => ({
            ...prev,
            [index]: { error: text },
          }));
        } else {
          throw new Error(text || "Failed to verify address");
        }
        return;
      }

      const data = JSON.parse(text);
      setSuggestedAddresses((prev) => ({ ...prev, [index]: data }));
    } catch (err) {
      toast({
        title: "Verification Failed",
        description: "Unable to verify address. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setVerifyingIndexes((prev) => prev.filter((i) => i !== index));
    }
  };

  const handleUseSuggested = (index) => {
    const suggestion = suggestedAddresses[index];
    if (!suggestion) return;
    skipVerificationRef.current.add(index);
    setValue(`ADDRESS.${index}.STREET_1`, suggestion.STREET1 || "");
    setValue(`ADDRESS.${index}.STREET_2`, suggestion.STREET2 || "");
    setValue(`ADDRESS.${index}.CITY`, suggestion.CITY || "");
    setValue(`ADDRESS.${index}.STATE`, suggestion.STATE || "");
    setValue(`ADDRESS.${index}.ZIP`, suggestion.ZIP || "");
    setSuggestedAddresses((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  useEffect(() => {
    addresses?.forEach((addr, index) => {
      const stateCode = addr?.STATE;
      if (stateCode && !countyOptionsMap[stateCode]) {
        const counties = scus.getCountiesByState(stateCode) || [];
        setCountyOptionsMap((prev) => ({
          ...prev,
          [stateCode]: counties.map((c) => ({
            value: `${c} County`,
            label: `${c} County`,
          })),
        }));
      }
    });
  }, [addresses]);

  return (
    <Box
      // w={{ base: "100%", md: "65%" }}

      p={5}
    >
      {/* Section Header */}
      <Flex align="center" justify="space-between" mb={5}>
        <Flex align="center" gap={2}>
          <Flex
            align="center"
            justify="center"
            w="30px"
            h="30px"
            borderRadius="8px"
            bg="blue.50"
            color="blue.500"
            flexShrink={0}
          >
            <Icon as={Icons.MapPinIcon} boxSize={4} />
          </Flex>
          <Text
            fontSize="13px"
            fontWeight="700"
            color="gray.700"
            letterSpacing="0.02em"
          >
            Address Information
          </Text>
          <Badge
            colorScheme="blue"
            fontSize="10px"
            borderRadius="full"
            px={2}
            py="1px"
          >
            {addressFields.length} address
            {addressFields.length !== 1 ? "es" : ""}
          </Badge>
        </Flex>
        <Tooltip label="Add new address" placement="left">
          <Flex
            align="center"
            gap={1.5}
            cursor="pointer"
            onClick={() =>
              prependAddress({
                TYPE: "HOME",
                PRIMARY: true,
                STREET_1: "",
                STREET_2: "",
                CITY: "",
                STATE: "",
                ZIP: "",
                COUNTY: "",
                COUNTRY: "",
                TELEPHONE_HOME: "",
                TELEPHONE_MOBILE: "",
                TELEPHONE_WORK: "",
              })
            }
            color="blue.500"
            fontSize="12px"
            fontWeight="600"
            px={3}
            py={1.5}
            borderRadius="8px"
            border="1px solid"
            borderColor="blue.100"
            bg="blue.50"
            _hover={{ bg: "blue.100", borderColor: "blue.200" }}
            transition="all 0.2s"
          >
            <Icon as={Icons.AdditionalDataIcon} boxSize={4} />
            <Text>Add Address</Text>
          </Flex>
        </Tooltip>
      </Flex>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        {addressFields?.map((field, index) => (
          <Box
            key={field.id}
            borderWidth="1px"
            borderColor="gray.100"
            p={4}
            borderRadius="12px"
            bg="white"
            position="relative"
            _hover={{
              borderColor: "blue.100",
              boxShadow: "0 2px 8px rgba(59,130,246,0.06)",
            }}
            transition="all 0.2s"
          >
            {/* Card Header */}
            <Flex justify="space-between" align="center" mb={4}>
              <Flex align="center" gap={2}>
                <Text
                  fontSize="12px"
                  fontWeight="700"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Address {index + 1}
                </Text>
                {addresses?.[index]?.TYPE && (
                  <Badge
                    colorScheme="purple"
                    fontSize="10px"
                    borderRadius="full"
                    px={2}
                  >
                    {addresses[index].TYPE}
                  </Badge>
                )}
              </Flex>
              <Tooltip label="Remove address" placement="top">
                <Icon
                  as={Icons.TrashIcon}
                  size="xs"
                  color="red"
                  aria-label="Remove address"
                  onClick={() => removeAddress(index)}
                  borderRadius="6px"
                  _hover={{ bg: "red.50" }}
                />
              </Tooltip>
            </Flex>

            {/* Address Fields */}
            <SimpleGrid columns={2} spacing={3}>
              {[
                "TYPE",
                "STREET_1",
                "STREET_2",
                "CITY",
                "STATE",
                "ZIP",
                "COUNTY",
                "COUNTRY",
              ].map((item) => {
                if (item === "STATE") {
                  return (
                    <FormControl key={item}>
                      <FieldLabel>State</FieldLabel>
                      <Select
                        options={usStateOptions}
                        isClearable
                        styles={modernSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={
                          usStateOptions.find(
                            (opt) =>
                              opt.value ===
                              addresses?.[index]?.STATE?.toUpperCase(),
                          ) || null
                        }
                        onChange={(selected) =>
                          setValue(
                            `ADDRESS.${index}.STATE`,
                            selected?.value || "",
                          )
                        }
                        placeholder="Select..."
                      />
                    </FormControl>
                  );
                }
                if (item === "COUNTY") {
                  const selectedState = addresses?.[index]?.STATE;
                  const countyOptions = countyOptionsMap[selectedState] || [];
                  return (
                    <FormControl key={item}>
                      <FieldLabel>County</FieldLabel>
                      <Select
                        options={countyOptions}
                        isClearable
                        styles={modernSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={
                          countyOptions.find(
                            (opt) => opt.value === addresses?.[index]?.COUNTY,
                          ) || null
                        }
                        onChange={(selected) =>
                          setValue(
                            `ADDRESS.${index}.COUNTY`,
                            selected?.value || "",
                          )
                        }
                        placeholder="Select..."
                      />
                    </FormControl>
                  );
                }
                if (item === "COUNTRY") {
                  return (
                    <FormControl key={item}>
                      <FieldLabel>Country</FieldLabel>
                      <Select
                        options={countryOptions}
                        isClearable
                        styles={modernSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={
                          countryOptions.find(
                            (opt) =>
                              opt.value ===
                              addresses?.[index]?.COUNTRY?.toUpperCase(),
                          ) || null
                        }
                        onChange={(selected) =>
                          setValue(
                            `ADDRESS.${index}.COUNTRY`,
                            selected?.value || "",
                          )
                        }
                        placeholder="Select..."
                      />
                    </FormControl>
                  );
                }
                if (item === "TYPE") {
                  return (
                    <FormControl key={item}>
                      <FieldLabel>Type</FieldLabel>
                      <Select
                        options={addressTypeOptions}
                        isClearable
                        styles={modernSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={
                          addressTypeOptions.find(
                            (opt) =>
                              opt.value ===
                              addresses?.[index]?.TYPE?.toUpperCase(),
                          ) || null
                        }
                        onChange={(selected) =>
                          setValue(
                            `ADDRESS.${index}.TYPE`,
                            selected?.value || "",
                          )
                        }
                        placeholder="Select..."
                      />
                    </FormControl>
                  );
                }
                return (
                  <FormControl key={item}>
                    <FieldLabel>{item.replace(/_/g, " ")}</FieldLabel>
                    <Input
                      {...register(`ADDRESS.${index}.${item}`)}
                      {...modernInputStyle}
                      placeholder={`Enter ${item.replace(/_/g, " ").toLowerCase()}`}
                    />
                  </FormControl>
                );
              })}
            </SimpleGrid>

            {/* Phone Fields */}
            <Box mt={3} pt={3} borderTop="1px dashed" borderColor="gray.200">
              <Flex align="center" gap={1.5} mb={3}>
                <Icon as={Icons.PhoneIcon} boxSize={4} color="#94A3B8" />
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Contact Numbers
                </Text>
              </Flex>
              <SimpleGrid columns={3} spacing={2}>
                {["TELEPHONE_HOME", "TELEPHONE_MOBILE", "TELEPHONE_WORK"].map(
                  (item) => (
                    <FormControl key={item}>
                      <FieldLabel>
                        {item === "TELEPHONE_HOME"
                          ? "Home"
                          : item === "TELEPHONE_MOBILE"
                            ? "Mobile"
                            : "Work"}
                      </FieldLabel>
                      <Flex gap={1}>
                        <InputMask
                          {...register(`ADDRESS.${index}.${item}`)}
                          mask="(999) 999-9999"
                        >
                          {(inputProps) => (
                            <Input
                              {...inputProps}
                              {...modernInputStyle}
                              placeholder="(000) 000-0000"
                              flex="1"
                            />
                          )}
                        </InputMask>
                        {item === "TELEPHONE_WORK" && (
                          <Input
                            {...modernInputStyle}
                            placeholder="Ext."
                            w="52px"
                            {...register(`ADDRESS.${index}.TELEPHONE_WORK_EXT`)}
                          />
                        )}
                      </Flex>
                    </FormControl>
                  ),
                )}
              </SimpleGrid>
            </Box>

            {/* Verification Status */}
            {verifyingIndexes.includes(index) ? (
              <Flex
                mt={3}
                p={3}
                align="center"
                gap={2}
                bg="blue.50"
                borderRadius="8px"
                border="1px solid"
                borderColor="blue.100"
              >
                <Spinner size="xs" color="blue.400" />
                <Text fontSize="12px" color="blue.600" fontWeight="500">
                  Verifying address...
                </Text>
              </Flex>
            ) : suggestedAddresses[index] ? (
              suggestedAddresses[index].error ? (
                <Flex
                  mt={3}
                  p={3}
                  align="flex-start"
                  gap={2}
                  bg="red.50"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="red.100"
                >
                  <Icon as={Icons.WarningIcon} boxSize={4} color="#F87171" />
                  <Text fontSize="12px" color="red.600" fontWeight="500">
                    {suggestedAddresses[index].error}
                  </Text>
                </Flex>
              ) : (
                <Flex
                  mt={3}
                  p={3}
                  align="center"
                  justify="space-between"
                  bg="green.50"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="green.100"
                  gap={2}
                >
                  <Flex align="flex-start" gap={2} flex={1}>
                    <Icon as={Icons.CheckIcon} boxSize={4} />
                    <Box>
                      <Text
                        fontSize="11px"
                        fontWeight="700"
                        color="green.700"
                        mb={0.5}
                      >
                        Suggested Address
                      </Text>
                      <Text fontSize="12px" color="green.600">
                        {[
                          suggestedAddresses[index].STREET1,
                          suggestedAddresses[index].STREET2,
                          suggestedAddresses[index].CITY,
                          suggestedAddresses[index].STATE,
                          suggestedAddresses[index].ZIP,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </Text>
                    </Box>
                  </Flex>
                  <Tooltip label="Use suggested address" placement="top">
                    <Flex
                      align="center"
                      gap={1}
                      cursor="pointer"
                      onClick={() => handleUseSuggested(index)}
                      bg="green.500"
                      color="white"
                      borderRadius="7px"
                      px={3}
                      py={1.5}
                      fontSize="12px"
                      fontWeight="600"
                      flexShrink={0}
                      _hover={{ bg: "green.600" }}
                      transition="all 0.15s"
                    >
                      <Icon as={Icons.CheckIcon} boxSize={4} />
                      <Text>Use</Text>
                    </Flex>
                  </Tooltip>
                </Flex>
              )
            ) : null}
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}
